import React, { useEffect, useRef, useState } from 'react';

import { Box, Button, Grid, Paper } from '@mui/material';

import { CrosshairIcon, WarningIcon } from '~/components/icons';
import { ImageErrorDialog } from '~/components/ImageErrorDialog';
import { CanvasProvider } from '~/contexts/CanvasContext';
import { useLoggedUser } from '~/contexts/LoggedUserContext';
import { ZOOM_CONFIG, useSidePanel } from '~/contexts/SidePanelContext';
import { useVehicleOptions } from '~/contexts/VehicleOptionsContext';
import { featureView } from '~/controllers/views';
import { ECameraType } from '~/domain/ECameraType';
import { ICamera } from '~/domain/ICamera';
import { ICreateImage } from '~/domain/ICreateImage';
import { ICreateIssue } from '~/domain/ICreateIssue';
import { SITE_CLIENT } from '~/env';
import * as LAYOUT from '~/pages/layout';
import { useAppDispatch, useAppSelector } from '~/redux';
import { hideDialog, showDialog } from '~/redux/features/imageErrorsSlice';

import { CameraView } from './CameraView';
// import { RetailerShotList } from './RetailerShotList';
import { FeatureShotList } from './FeatureShotList';
import { FeatureShotViewer } from './FeatureShotViewer';
import {
  CENTERPANO_IMAGE_SETTINGS,
  DEALER_SHOT_IMAGE_SETTINGS,
  EXTERIOREXT360_IMAGE_SETTINGS,
  EXTERIOREXT360_PREVIEW_IMAGE_SETTINGS,
  EXTERIORSTUDIO360_IMAGE_SETTINGS,
  EXTERIORSTUDIO360_PREVIEW_IMAGE_SETTINGS,
  FEATURE_SHOT_IMAGE_SETTINGS,
  FEATURE_SHOT_LIST_IMAGE_SETTINGS,
  INTERIOR360_IMAGE_SETTINGS,
  INTERIOR360_PREVIEW_IMAGE_SETTINGS,
} from './imageSettings';
import { SidePanel } from './SidePanel';
import { Use360Viewer } from './Use360Viewer';
import { VehiclePanorama } from './VehiclePanorama';

interface IRequestPage {
  cameras: ICamera[];
  camera?: ICamera;
}

const REQUEST_HEADER_HEIGHT = 60;
const ANNOTATION_TOOLBAR_HEIGHT = 116;
//const resolveView = (cameras: ICamera[], camera?: ICamera, dude?:string) => {
const resolveView = (cameras: ICamera[], camera?: ICamera) => {
  //console.log('resolveView camera: ', camera);
  if (
    !camera &&
    cameras.length > 1 &&
    !!cameras.find((value) => featureView.types.includes(value.type))
  ) {
    // if there is no camera selected and the cameras-manifest has one of the types for the feature shots
    return 'list';
  } else {
    if (!camera && cameras.length > 0) {
      // if there is no camera selected but we have cameras-manifest entries, select based on the first from the manifest
      if (
        cameras[0].type === ECameraType.EXT_360 ||
        cameras[0].type === ECameraType.EXT_ENV_360 ||
        cameras[0].type === ECameraType.EXT_STUDIO_360
      ) {
        return 'E360';
      }
      if (cameras[0].type === ECameraType.INT_360) {
        return 'I360';
      }
      if (
        cameras[0].type === ECameraType.INT_PANO ||
        cameras[0].type === ECameraType.INT_PANO_STUDIO_SEATS_UP ||
        cameras[0].type === ECameraType.INT_PANO_STUDIO_SEATS_DOWN ||
        cameras[0].type === ECameraType.INT_PANO_ENV_SEATS_UP ||
        cameras[0].type === ECameraType.INT_PANO_ENV_SEATS_DOWN
      ) {
        return 'pano';
      }
      if (cameras[0].type === ECameraType.FEATURE_RETAILER) {
        return 'list';
      }
    }
  }
  // if we have one camera selected, choose based on that one
  if (
    camera?.type === ECameraType.EXT_360 ||
    camera?.type === ECameraType.EXT_ENV_360 ||
    camera?.type === ECameraType.EXT_STUDIO_360
  ) {
    return 'E360';
  }
  if (camera?.type === ECameraType.INT_360) {
    return 'I360';
  }

  if (camera?.type === ECameraType.FEATURE_RETAILER) {
    if (window.location.href.toLowerCase().indexOf('/retailer') > -1) {
      return 'list';
    } else {
      return 'shot';
    }
  }

  if (
    camera?.type === ECameraType.INT_PANO ||
    camera?.type === ECameraType.INT_PANO_STUDIO_SEATS_UP ||
    camera?.type === ECameraType.INT_PANO_STUDIO_SEATS_DOWN ||
    camera?.type === ECameraType.INT_PANO_ENV_SEATS_UP ||
    camera?.type === ECameraType.INT_PANO_ENV_SEATS_DOWN
  ) {
    return 'pano';
  }
  if (camera) {
    return 'shot';
  }
};

export const RequestPageBody = React.memo<IRequestPage>(
  function RequestPageBody(props) {
    const dispatch = useAppDispatch();

    const imageErrors = useAppSelector((state) => state.imageErrors.errors);
    const errorDialogIsOpen = useAppSelector(
      (state) => state.imageErrors.isOpen
    );

    const { cameras } = props;
    const { camera } = props;
    const sidePanel = useSidePanel();
    const vehicleOptions = useVehicleOptions();
    const { eim, features } = vehicleOptions;

    const loggedUser = useLoggedUser();

    const view = resolveView(cameras, camera);
    const handleCreateIssue = () => {
      const issue: ICreateIssue = {
        title: '',
        description: '',
        issueCategory: '',
        images: [],
        features,
        affectedEims: eim,
      };
      sidePanel.dispatch('create-issue', {
        attachTo: issue as unknown as Record<string, string | ICreateImage[]>,
      });
    };
    const ref = useRef() as React.MutableRefObject<HTMLDivElement>;
    const [imageContainerHeight, setImageContainerHeight] = useState<number>(
      window.innerHeight - ANNOTATION_TOOLBAR_HEIGHT - REQUEST_HEADER_HEIGHT
    );
    useEffect(() => {
      const wrapper = ref.current;
      const resize = () => {
        const { offsetHeight } = wrapper;
        setImageContainerHeight(
          offsetHeight - ANNOTATION_TOOLBAR_HEIGHT - REQUEST_HEADER_HEIGHT
        );
      };
      resize();
      const observer = new ResizeObserver(resize);
      observer.observe(wrapper);
      return () => {
        observer.unobserve(wrapper);
        observer.disconnect();
      };
    }, []);

    return (
      <Box>
        <Grid
          container
          columnSpacing={1}
          alignItems="stretch"
          sx={{
            height: 'calc(100vh - 96px)',
            '> div': {
              height: '100%',
            },
          }}
        >
          <CanvasProvider zoomConfig={ZOOM_CONFIG[view || '']}>
            <Grid
              ref={ref}
              item
              xs={9}
              sx={[
                LAYOUT.CONTAINER,
                {
                  maxWidth: 'calc(100% - 590px) !important',
                },
              ]}
            >
              <Paper
                component="nav"
                sx={[
                  LAYOUT.HEADER,
                  {
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'stretch',
                    padding: '8px',
                    mb: 1,
                  },
                ]}
              >
                <CameraView camera={camera} cameras={cameras} />
                {SITE_CLIENT !== 'Subaru' && (
                  <Button
                    size="small"
                    startIcon={<CrosshairIcon sx={{ width: 12, height: 12 }} />}
                    onClick={handleCreateIssue}
                  >
                    Add Issue
                  </Button>
                )}
                {loggedUser?.validation && imageErrors?.length > 0 && (
                  <Button
                    size="small"
                    color="error"
                    variant="contained"
                    startIcon={<WarningIcon sx={{ width: 12, height: 12 }} />}
                    onClick={() => dispatch(showDialog())}
                  >
                    Errors
                  </Button>
                )}
                <ImageErrorDialog
                  open={errorDialogIsOpen}
                  onClose={() => dispatch(hideDialog())}
                />
              </Paper>
              {view === 'list' && (
                <FeatureShotList
                  camera={camera}
                  cameras={cameras}
                  settings={FEATURE_SHOT_LIST_IMAGE_SETTINGS}
                />
              )}
              {view === 'E360' && (
                <Use360Viewer
                  camera={
                    camera ||
                    cameras.filter((x) => x.type === ECameraType.EXT_360)[0] ||
                    cameras.filter(
                      (x) => x.type === ECameraType.EXT_STUDIO_360
                    )[0] ||
                    cameras.filter((x) => x.type === ECameraType.EXT_ENV_360)[0]
                  }
                  settings={
                    camera?.type === ECameraType.EXT_STUDIO_360
                      ? EXTERIORSTUDIO360_IMAGE_SETTINGS
                      : EXTERIOREXT360_IMAGE_SETTINGS
                  }
                  settings4K={
                    camera?.type === ECameraType.EXT_STUDIO_360
                      ? EXTERIORSTUDIO360_PREVIEW_IMAGE_SETTINGS
                      : EXTERIOREXT360_PREVIEW_IMAGE_SETTINGS
                  }
                  containerWidth={
                    camera?.type === ECameraType.EXT_STUDIO_360
                      ? imageContainerHeight *
                        EXTERIOREXT360_IMAGE_SETTINGS.aspectRatio
                      : imageContainerHeight *
                        EXTERIORSTUDIO360_IMAGE_SETTINGS.aspectRatio
                  }
                />
              )}
              {view === 'I360' && (
                <Use360Viewer
                  camera={
                    camera ||
                    cameras.filter((x) => x.type === ECameraType.INT_360)[0]
                  }
                  settings={INTERIOR360_IMAGE_SETTINGS}
                  settings4K={INTERIOR360_PREVIEW_IMAGE_SETTINGS}
                  containerWidth={
                    imageContainerHeight *
                    INTERIOR360_IMAGE_SETTINGS.aspectRatio
                  }
                />
              )}
              {view === 'pano' && (
                <VehiclePanorama
                  camera={
                    camera ||
                    cameras.filter((x) => x.type === ECameraType.INT_PANO)[0]
                  }
                  containerWidth={
                    imageContainerHeight * CENTERPANO_IMAGE_SETTINGS.aspectRatio
                  }
                  settings={CENTERPANO_IMAGE_SETTINGS}
                />
              )}
              {view === 'shot' && camera && (
                <FeatureShotViewer
                  camera={camera}
                  cameras={cameras.filter((x) =>
                    [
                      ECameraType.FEATURE_RETAILER,
                      ECameraType.FEATURE_EXT,
                      ECameraType.FEATURE_INT,
                    ].includes(x.type)
                  )}
                  containerWidth={
                    camera.type === ECameraType.FEATURE_RETAILER
                      ? imageContainerHeight *
                        DEALER_SHOT_IMAGE_SETTINGS.aspectRatio
                      : imageContainerHeight *
                        FEATURE_SHOT_IMAGE_SETTINGS.aspectRatio
                  }
                  settings={
                    camera.type === ECameraType.FEATURE_RETAILER
                      ? DEALER_SHOT_IMAGE_SETTINGS
                      : FEATURE_SHOT_IMAGE_SETTINGS
                  }
                />
              )}
            </Grid>
            <Grid item xs={3} sx={[LAYOUT.CONTAINER, { minWidth: '590px' }]}>
              <SidePanel />
            </Grid>
          </CanvasProvider>
        </Grid>
      </Box>
    );
  }
);

/*
imageContainerHeight *
                  FEATURE_SHOT_IMAGE_SETTINGS.aspectRatio
*/
